import { toDashboardUiError } from '@/lib/dashboard-errors';
import { supabase } from '@/lib/supabase';
import type {
  ToolAppCommunicationRequest,
  ToolAppCommunicationResponse,
} from '@shared/types/tool-app-communications';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isMentionLike = (value: unknown): boolean => (
  isRecord(value)
  && (value.principalKind === 'user' || value.principalKind === 'team')
  && typeof value.principalId === 'string'
  && typeof value.label === 'string'
);

const isMessageLike = (value: unknown): boolean => (
  isRecord(value)
  && typeof value.id === 'string'
  && typeof value.body === 'string'
  && typeof value.createdAt === 'string'
  && isRecord(value.author)
  && typeof value.author.userId === 'string'
  && typeof value.author.displayName === 'string'
  && Array.isArray(value.mentions)
  && value.mentions.every(isMentionLike)
);

const isPrincipalLike = (value: unknown): boolean => (
  isRecord(value)
  && (value.principalKind === 'user' || value.principalKind === 'team')
  && typeof value.principalId === 'string'
  && typeof value.label === 'string'
  && typeof value.detail === 'string'
);

const isCommunicationResponse = (value: unknown): value is ToolAppCommunicationResponse => (
  isRecord(value)
  && (value.threadId === null || typeof value.threadId === 'string')
  && Array.isArray(value.messages)
  && value.messages.every(isMessageLike)
  && Array.isArray(value.mentionablePrincipals)
  && value.mentionablePrincipals.every(isPrincipalLike)
);

export const invokeToolAppCommunication = async (
  request: ToolAppCommunicationRequest,
): Promise<ToolAppCommunicationResponse> => {
  const { data, error } = await supabase.functions.invoke('tool-app-communicate', {
    body: request,
  });

  if (error) {
    throw await toDashboardUiError(error, 'Impossible de charger la conversation pour le moment.');
  }
  if (!isCommunicationResponse(data)) {
    throw new Error('Réponse invalide de tool-app-communicate.');
  }
  return data;
};
